import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, from } from 'rxjs';
import { concatMap, debounceTime, map, startWith, toArray } from 'rxjs/operators';
import { CodaltComponent } from 'src/app/codalt.component';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { VkmExcelExportService } from 'src/app/services/vkm-excel-export.service';
import { VkmService, Workorder } from 'src/app/services/vkm.service';
import { DAY_IN_MILLISECONDS, Utils } from 'src/app/utils.class';

@Component({
    selector: 'app-vkm-workorders',
    templateUrl: './vkm-workorders.component.html',
    styleUrls: ['./vkm-workorders.component.scss'],
    standalone: false,
})
export class VkmWorkordersComponent extends CodaltComponent implements OnInit {
    generatingExcel = false;

    VkmStatus = this.environment.status.Vkm;

    fcSearch = new FormControl();
    fcShowHistory = new FormControl();
    fcGroupByProject = new FormControl();
    fcSelectedProjects = new FormControl<number[]>([]);

    workorders: Workorder[];
    groupedWorkorders: GroupedWorkorder[];
    private readonly FILTER_STORAGE_KEY = 'vkmWorkordersFilters';

    constructor(
        private vkmService: VkmService,
        private entitiesService: EntitiesService,
        private vkmExcelExportService: VkmExcelExportService,
        private router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.loadSavedFilters();

        this.subscriptions.add(
            combineLatest([
                this.fcSearch.valueChanges.pipe(debounceTime(200), startWith(this.fcSearch.value || '')),
                this.fcShowHistory.valueChanges.pipe(debounceTime(50), startWith(this.fcShowHistory.value || false)),
                this.fcGroupByProject.valueChanges.pipe(
                    debounceTime(50),
                    startWith(this.fcGroupByProject.value || false)
                ),
            ]).subscribe(([search, history, groupByProject]) => {
                this.saveCurrentFilters();

                if (!groupByProject) {
                    this.fcSelectedProjects.setValue([]);
                }

                this.subscriptions.add(
                    this.vkmService.getWorkorderList(search, history).subscribe(workorders => {
                        this.workorders = workorders.data;
                        if (groupByProject) {
                            this.groupedWorkorders = this.groupWorkordersByProject(this.workorders);
                        } else {
                            this.groupedWorkorders = null;
                        }
                    })
                );
            })
        );
    }

    excel() {
        if (this.fcSelectedProjects.value && this.fcSelectedProjects.value.length > 0) {
            this.generatingExcel = true;
            from(this.fcSelectedProjects.value)
                .pipe(
                    concatMap((planningId: number) => this.vkmService.countsAndPrices(planningId)),
                    map(result => result.data),
                    toArray()
                )
                .subscribe(allResults => {
                    const allData = allResults.flat();
                    const objectsMap = new Map<number, any>();
                    const materialsMap = new Map<number, any>();
                    const settleGroupPricesMap = new Map<string, number>();
                    let totalPrice = 0;

                    allData.forEach(data => {
                        data.objects.forEach(obj => {
                            if (objectsMap.has(obj.entity_id)) {
                                const existing = objectsMap.get(obj.entity_id);
                                existing.count_lost = (existing.count_lost ?? 0) + +(obj.count_lost ?? 0);
                                existing.count_placed = (existing.count_placed ?? 0) + +(obj.count_placed ?? 0);
                                existing.count_removed = (existing.count_removed ?? 0) + +(obj.count_removed ?? 0);
                                existing.price_total = (existing.price_total ?? 0) + +(obj.price_total ?? 0);
                                existing.price_unit_total =
                                    (existing.price_unit_total ?? 0) + +(obj.price_unit_total ?? 0);
                                existing.total_days = (existing.total_days ?? 0) + +(obj.total_days ?? 0);
                                existing.days = Math.max(existing.days ?? 0, +(obj.days ?? 0));

                                if (
                                    obj.date_placed &&
                                    (!existing.date_placed ||
                                        new Date(obj.date_placed) < new Date(existing.date_placed))
                                ) {
                                    existing.date_placed = obj.date_placed;
                                }
                                if (
                                    obj.date_removed &&
                                    (!existing.date_removed ||
                                        new Date(obj.date_removed) > new Date(existing.date_removed))
                                ) {
                                    existing.date_removed = obj.date_removed;
                                }
                            } else {
                                objectsMap.set(obj.entity_id, { ...obj });
                            }
                        });
                        data.materials.forEach(mat => {
                            if (materialsMap.has(mat.entity_id)) {
                                const existing = materialsMap.get(mat.entity_id);
                                existing.count_placed += mat.count_placed;
                                existing.price_total += mat.price_total;
                                existing.price_unit_total += mat.price_unit_total;
                            } else {
                                materialsMap.set(mat.entity_id, { ...mat });
                            }
                        });
                        data.totalPrices.forEach(settlePrice => {
                            if (settleGroupPricesMap.has(settlePrice.settle_group)) {
                                settleGroupPricesMap.set(
                                    settlePrice.settle_group,
                                    settleGroupPricesMap.get(settlePrice.settle_group) + settlePrice.price
                                );
                            } else {
                                settleGroupPricesMap.set(settlePrice.settle_group, settlePrice.price);
                            }
                        });
                        totalPrice += data.totalPrice;
                    });

                    const aggregatedData = {
                        drawingNrList: allResults.map(result => result.drawingNrList).flat(),
                        objects: Array.from(objectsMap.values()),
                        materials: Array.from(materialsMap.values()),
                        totalPrices: Array.from(settleGroupPricesMap.entries()).map(([settle_group, price]) => ({
                            settle_group,
                            price,
                        })),
                        totalPrice,
                    };
                    this.subscriptions.add(
                        this.entitiesService.getMap().subscribe(entitiesMap => {
                            this.vkmExcelExportService
                                .createExcelExport(
                                    aggregatedData,
                                    entitiesMap,
                                    this.groupedWorkorders.filter(group =>
                                        this.fcSelectedProjects.value.includes(group.planning_id)
                                    )
                                )
                                .then(
                                    () => {
                                        this.generatingExcel = false;
                                    },
                                    () => {
                                        this.generatingExcel = false;
                                    }
                                );
                        })
                    );
                });
        }
    }

    private saveCurrentFilters(): void {
        const filters = {
            search: this.fcSearch.value || '',
            showHistory: this.fcShowHistory.value || false,
            groupByProject: this.fcGroupByProject.value || false,
            timestamp: Date.now(),
        };

        try {
            sessionStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify(filters));
        } catch (error) {
            console.warn('Could not save filters to sessionStorage:', error);
        }
    }

    private loadSavedFilters(): void {
        try {
            const saved = sessionStorage.getItem(this.FILTER_STORAGE_KEY);
            if (saved) {
                const filters = JSON.parse(saved);
                // Check if filters are not too old (expire after 24 hours)
                const maxAge = DAY_IN_MILLISECONDS; // 24 hours in milliseconds
                if (filters.timestamp && Date.now() - filters.timestamp < maxAge) {
                    this.fcSearch.setValue(filters.search || '');
                    this.fcShowHistory.setValue(filters.showHistory || false);
                    this.fcGroupByProject.setValue(filters.groupByProject || false);
                }
            }
        } catch (error) {
            console.warn('Could not load filters from sessionStorage:', error);
        }
    }

    private groupWorkordersByProject(workorders: Workorder[]): GroupedWorkorder[] {
        const grouped = new Map<number, GroupedWorkorder>();

        workorders.forEach(workorder => {
            const planningId = workorder.planning_id;

            if (!grouped.has(planningId)) {
                grouped.set(planningId, {
                    afas_project_id: workorder.afas_project_id,
                    description: workorder.description,
                    contractor: workorder.contractor,
                    location: workorder.location,
                    type: workorder.type,
                    workorders: [],
                    minDate: new Date(workorder.begindate),
                    maxDate: new Date(workorder.enddate || workorder.begindate),
                    planning_id: workorder.planning_id,
                });
            }

            const group = grouped.get(planningId);
            group.workorders.push(workorder);

            const beginDate = new Date(workorder.begindate);
            const endDate = new Date(workorder.enddate || workorder.begindate);

            if (beginDate < group.minDate) {
                group.minDate = beginDate;
            }
            if (endDate > group.maxDate) {
                group.maxDate = endDate;
            }
        });

        return Array.from(grouped.values());
    }

    open(planningHas: Workorder, event?: MouseEvent) {
        const route = [
            `vkm/${planningHas.planning_id}`,
            {
                backTo: `vkm-werkorders`,
                planningHas: planningHas.id,
            },
        ];

        if (event && event.ctrlKey) {
            const url = this.router.serializeUrl(this.router.createUrlTree(route));
            Utils.openTab(url);
        } else {
            this.router.navigate(route);
        }
    }

    openGrouped(groupedWorkorder: GroupedWorkorder, event?: MouseEvent) {
        const firstWorkorder = groupedWorkorder.workorders[0];
        this.open(firstWorkorder, event);
    }

    isProjectSelected(planningId: number): boolean {
        const selectedProjects = this.fcSelectedProjects.value || [];
        return selectedProjects.includes(planningId);
    }

    toggleProjectSelection(planningId: number, event: Event): void {
        const selectedProjects = this.fcSelectedProjects.value || [];
        const index = selectedProjects.indexOf(planningId);

        if (index > -1) {
            const newSelection = selectedProjects.filter(id => id !== planningId);
            this.fcSelectedProjects.setValue(newSelection);
        } else {
            this.fcSelectedProjects.setValue([...selectedProjects, planningId]);
        }
    }

    toggleAllSelection(event: any): void {
        if (event.checked) {
            const allPlanningIds = this.groupedWorkorders.map(group => group.planning_id);
            this.fcSelectedProjects.setValue(allPlanningIds);
        } else {
            this.fcSelectedProjects.setValue([]);
        }
    }
}

export interface GroupedWorkorder {
    afas_project_id: string;
    description: string;
    contractor: string;
    location: string;
    type: string;
    workorders: Workorder[];
    minDate: Date;
    maxDate: Date;
    planning_id: number;
}
